import {
  useNavigate } from "react-router-dom";
import { ArrowRight,
  TrendingUp,
  Search,
  X,
  Package } from "lucide-react";
import {
  Card,
  CardContent,
  Badge,
  Input,
  Button
} from "@b2b/ui-components";
import { useEffect, useState } from "react";
import { adminService } from "@b2b/services";
import { useAuth } from "../../auth/Authprovider";
import { useChatUnreadHeader } from "../../chat/ChatUnreadHeaderProvider";
import { BizcoverIndiaFigmaHeader } from "../../components/marketing/BizcoverIndiaFigmaHeader";
import { BizcoverIndiaFigmaFooter } from "../../components/marketing/BizcoverIndiaFigmaFooter";

const Categories = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const chatUnread = useChatUnreadHeader();
  const [categories, setCategories] = useState<any[]>([]);
  const [allCategories, setAllCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Filter categories based on search
  useEffect(() => {
    if (!debouncedSearch.trim()) {
      setCategories(allCategories);
      return;
    }

    const filtered = allCategories.filter((category: any) =>
      category.name?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      category.description?.toLowerCase().includes(debouncedSearch.toLowerCase())
    );
    setCategories(filtered);
  }, [debouncedSearch, allCategories]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    adminService
      .getCategories(true)
      .then((data) => {
        if (!active) return;
        const topLevel = Array.isArray(data)
          ? data.filter((c: any) => !c.parentId)
          : [];
        // Filter to only show categories with products
        const categoriesWithProducts = topLevel.filter(
          (c: any) => (c._count?.products || c.productCount || 0) > 0
        );
        setAllCategories(categoriesWithProducts);
        setCategories(categoriesWithProducts);
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        setError("Failed to load categories");
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const handleSearch = () => {
    setDebouncedSearch(searchQuery);
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setDebouncedSearch("");
  };

  return (
    <div className="min-h-screen bg-background">
      <BizcoverIndiaFigmaHeader />
      {/* Alibaba-Style Hero Section */}
      <section className="bg-white py-12 border-b border-gray-200">
        <div className="container mx-auto px-4">
          {/* Subtle Heading */}
          <h1 className="text-2xl md:text-3xl font-bold mb-6 text-center text-foreground">
            Browse Product Categories
          </h1>

          {/* Search Bar */}
          <div className="max-w-3xl mx-auto mb-4">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                <Input
                  type="text"
                  placeholder="Search categories by name or description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleSearch();
                    }
                  }}
                  className="pl-10 pr-10 h-12 text-base"
                />
                {searchQuery && (
                  <button
                    onClick={handleClearFilters}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    type="button"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <Button
                onClick={handleSearch}
                variant="primary"
                className="h-12 px-6 whitespace-nowrap"
              >
                <Search className="h-4 w-4 mr-2" />
                Search
              </Button>
            </div>
          </div>

          {/* Stats - Compact */}
          <div className="flex items-center justify-center gap-4 text-sm text-gray-600 max-w-3xl mx-auto">
            <div className="flex items-center gap-1.5">
              <Package className="h-4 w-4 text-gray-500" />
              <span className="font-medium">
                {categories.length} Categories
              </span>
            </div>
          </div>
        </div>
      </section>
      {/* Categories Grid - Alibaba Style */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="text-center py-12 text-lg">
              Loading categories...
            </div>
          ) : error ? (
            <div className="text-center py-12 text-red-600">{error}</div>
          ) : categories.length === 0 ? (
            <div className="text-center py-12 text-gray-600">
              No categories found. Try adjusting your search.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
              {categories.map((category: any, index: number) => (
                <Card
                  key={category.id || category.slug || index}
                  className="group cursor-pointer border border-gray-200 hover:border-orange-500 hover:shadow-lg transition-all duration-300 bg-white overflow-hidden"
                  onClick={() => {
                    // If category has children, navigate to show level 2 categories
                    // Otherwise, navigate to search page with category name
                    if (category.children && category.children.length > 0) {
                      navigate(`/categories/${category.slug || category.id}`);
                    } else {
                      // No children, navigate to search for products in this category
                      navigate(`/search/${encodeURIComponent(category.name)}`);
                    }
                  }}
                >
                  {/* Image Section - Alibaba Style: Square/Compact */}
                  <div className="relative w-full aspect-square overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
                    <img
                      src={
                        category.coverImage ||
                        category.banner ||
                        category.icon ||
                        ""
                      }
                      alt={category.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      onError={(e) => {
                        // Fallback to gradient background if image fails
                        e.currentTarget.style.display = "none";
                      }}
                    />
                    
                    {/* Trending Badge - Top Right */}
                    {category.trending && (
                      <div className="absolute top-2 right-2">
                        <Badge
                          variant="default"
                          className="bg-orange-600 text-white text-xs px-2 py-0.5 flex items-center gap-1"
                        >
                          <TrendingUp className="h-3 w-3" />
                        </Badge>
                      </div>
                    )}
                  </div>

                  {/* Content Section - Minimal Alibaba Style */}
                  <CardContent className="p-4">
                    {/* Category Name */}
                    <h3 className="text-base font-semibold text-foreground group-hover:text-orange-600 transition-colors mb-2 line-clamp-2 min-h-[2.5rem]">
                      {category.name}
                    </h3>

                    {/* Product Count - Prominent */}
                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                      <div className="flex items-center gap-1.5">
                        <Package className="h-3.5 w-3.5 text-gray-500" />
                        <span className="text-sm font-medium text-gray-700">
                          {(
                            category._count?.products ||
                            category.productCount ||
                            0
                          ).toLocaleString()}
                        </span>
                        <span className="text-xs text-gray-500">Products</span>
                      </div>
                      <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-orange-600 group-hover:translate-x-1 transition-all" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
      <BizcoverIndiaFigmaFooter />
    </div>
  );
};

export default Categories;
